import * as Sentry from '@sentry/vue';
import { ref } from 'vue';
import type { Ref } from 'vue';
import type {
  CommitmentResponse,
  PurchaseResponse,
  RegistrationAttendeeResponse,
} from '@@/types/api';
import { ApiError } from '~/composables/api/api-error';
import { useRegistrationApi } from '~/composables/api/use-registration-api';

interface UseInviteActionsOptions {
  commitment: Ref<CommitmentResponse | null>;
  purchase: Ref<PurchaseResponse | null>;
}

export function useInviteActions(options: UseInviteActionsOptions) {
  const { commitment, purchase } = options;
  const registrationApi = useRegistrationApi();

  const attendees = ref<RegistrationAttendeeResponse[]>([]);
  const isLoadingAttendees = ref(false);
  const isInviting = ref(false);
  const isRescinding = ref(false);
  const inviteError = ref<string | null>(null);

  const snackbar = ref(false);
  const snackbarText = ref('');
  const snackbarColor = ref('success');

  function showSnackbar(text: string, color = 'success') {
    snackbarText.value = text;
    snackbarColor.value = color;
    snackbar.value = true;
  }

  async function fetchAttendees() {
    if (!commitment.value && !purchase.value) {
      attendees.value = [];
      return;
    }
    isLoadingAttendees.value = true;
    try {
      if (commitment.value) {
        const response = await registrationApi.getCommitmentAttendees(
          commitment.value.id,
        );
        attendees.value = response.data;
      } else if (purchase.value) {
        const response = await registrationApi.getPurchaseAttendees(
          purchase.value.id,
        );
        attendees.value = response.data;
      }
    } catch (e) {
      Sentry.captureException(e);
      attendees.value = [];
    } finally {
      isLoadingAttendees.value = false;
    }
  }

  async function sendInvite(email: string) {
    isInviting.value = true;
    inviteError.value = null;
    try {
      if (commitment.value) {
        await registrationApi.inviteToCommitment(commitment.value.id, {
          email,
        });
      } else if (purchase.value) {
        await registrationApi.inviteToPurchase(purchase.value.id, { email });
      } else {
        return;
      }
      await fetchAttendees();
      showSnackbar(`Invite sent to ${email}`);
    } catch (e: unknown) {
      Sentry.captureException(e);
      if (e instanceof ApiError && e.isNetworkError) {
        inviteError.value =
          'Network error — check your connection and try again.';
        showSnackbar(inviteError.value, 'error');
      } else {
        const err = e as { message?: string };
        inviteError.value = err.message ?? 'Failed to send invite.';
        showSnackbar(inviteError.value, 'error');
      }
    } finally {
      isInviting.value = false;
    }
  }

  async function rescindInvite(attendeeId: string) {
    isRescinding.value = true;
    try {
      await registrationApi.rescindInvite(attendeeId);
      await fetchAttendees();
      showSnackbar('Invite rescinded');
    } catch (e) {
      Sentry.captureException(e);
      const isNetwork = e instanceof ApiError && e.isNetworkError;
      showSnackbar(
        isNetwork
          ? 'Network error — check your connection and try again.'
          : 'Failed to rescind invite.',
        'error',
      );
    } finally {
      isRescinding.value = false;
    }
  }

  function clearError() {
    inviteError.value = null;
  }

  return {
    attendees,
    isLoadingAttendees,
    isInviting,
    isRescinding,
    inviteError,
    snackbar,
    snackbarText,
    snackbarColor,
    fetchAttendees,
    sendInvite,
    rescindInvite,
    clearError,
  };
}
